import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

import type { PaymentStatusChange } from "@/modules/dashboard/payments-activity/services/paymentsStatusChanges.service";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

export interface LedgerStatusTrailProps {
  changes: PaymentStatusChange[] | null;
  isLoading: boolean;
  hasError: boolean;
}

const FAILED_STATUSES = new Set(["REJECTED", "FAILED", "REVERSED", "CANCELLED"]);

/**
 * The operation's status history, collapsed by default, next to its journal entries.
 *
 * Reconciling means answering "when did the runtime decide this, and what did it do to the money" — the
 * entries cover the second half and this covers the first. The source is labeled in Coloca terms
 * (`provider_webhook` → "Confirmación del proveedor"); no provider name or reference is rendered (rule 12),
 * and the `metadata.ledger_entry_id` a transition may carry is not shown as an id either: the entry cards are
 * right below, in the same chronological order.
 */
export function LedgerStatusTrail({ changes, isLoading, hasError }: LedgerStatusTrailProps) {
  const { t, activeLanguage } = useTranslations("paymentsLedger");
  const [isOpen, setIsOpen] = useState(false);

  // The section is an enhancement: when the log cannot be read there is nothing useful to disclose here.
  if (hasError) return null;

  const sourceLabel: Record<string, string> = {
    internal_api: t("trail_source_internal_api"),
    provider_webhook: t("trail_source_provider_webhook"),
    system: t("trail_source_system"),
  };

  return (
    <section className="rounded-xl border border-border bg-card">
      <header className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">{t("trail_title")}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("trail_description")}</p>
        </div>
        <ColocaButton
          variant="default"
          size="sm"
          iconTrailing={isOpen ? ChevronUp : ChevronDown}
          onClick={() => setIsOpen((open) => !open)}
        >
          {isOpen ? t("trail_toggle_hide") : t("trail_toggle_show")}
        </ColocaButton>
      </header>

      {isOpen && (
        <div className="border-t border-border p-4">
          {isLoading ? (
            <div className="space-y-2" aria-hidden="true">
              <ColocaSkeleton className="h-4 w-2/3" />
              <ColocaSkeleton className="h-4 w-1/2" />
            </div>
          ) : !changes || changes.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("trail_empty")}</p>
          ) : (
            <ol className="space-y-0">
              {changes.map((change, index) => {
                const failed = FAILED_STATUSES.has(change.new_status);
                const caption = [sourceLabel[change.source] ?? change.source, change.reason]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <li key={change.id} className="relative flex gap-3 pb-4 last:pb-0">
                    {index < changes.length - 1 && (
                      <span
                        aria-hidden="true"
                        className="absolute left-[3.5px] top-4 h-full w-px bg-border"
                      />
                    )}
                    <span
                      aria-hidden="true"
                      className={cn(
                        "mt-1.5 size-2 shrink-0 rounded-full",
                        failed ? "bg-destructive" : "bg-success",
                      )}
                    />
                    <div className="min-w-0">
                      <p className="font-mono text-sm font-medium text-foreground">
                        {change.previous_status
                          ? `${change.previous_status} → ${change.new_status}`
                          : `${t("trail_initial")} → ${change.new_status}`}
                      </p>
                      {caption && <p className="text-xs text-muted-foreground">{caption}</p>}
                      <p className="font-mono text-[11px] text-muted-foreground tabular-nums">
                        {formatDateTime(change.created_at, { locale: activeLanguage, includeTime: true })}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}
